import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Star } from 'lucide-react'
import { toast } from 'sonner'
import { useNavigate } from '@tanstack/react-router'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useIsStarred, useProjectMutations, useStarredMutations } from '@/lib/db/rxdb/hooks'
import { UnifiedIconPicker } from '@/components/app/pickers/unified-icon-picker'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { cn } from '@/lib/utils'
import { generateRankBetween } from '@/lib/utils/lexorank'

// Space and project dialogs shared by the drill-in sidebar (design 21 C).

type TFunc = (key: string) => string

const buildProjectFormSchema = (t: TFunc) =>
  z.object({
    name: z
      .string()
      .min(1, t('sidebar.formErrors.projectNameRequired'))
      .max(100, t('sidebar.formErrors.projectNameTooLong')),
    projectKey: z
      .string()
      .min(1, t('sidebar.formErrors.projectKeyRequired'))
      .max(10, t('sidebar.formErrors.projectKeyTooLong'))
      .regex(/^[A-Z0-9]+$/, t('sidebar.formErrors.projectKeyFormat')),
    icon: z.string().nullable(),
    iconType: z.string().nullable(),
    iconColor: z.string().nullable(),
  })

type ProjectFormData = z.infer<ReturnType<typeof buildProjectFormSchema>>

// ─── Space details drawer ─────────────────────────────────────────────────────

interface SpaceDetailsDrawerProps {
  space: ProjectDocument
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SpaceDetailsDrawer({ space, open, onOpenChange }: SpaceDetailsDrawerProps) {
  const { t } = useTranslation('projects')
  const { date: fmtDate } = useDateTimeFormatters()
  const formatDate = (ts: number | undefined) => {
    if (!ts) return '—'
    return fmtDate(new Date(ts), { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatRelative = (ts: number | undefined) => {
    if (!ts) return ''
    const diff = Date.now() - ts
    const hours = Math.floor(diff / 3_600_000)
    const days = Math.floor(diff / 86_400_000)
    if (hours < 1) return t('sidebar.space.justNow')
    if (hours < 24) return t('sidebar.space.hoursAgo', { count: hours })
    if (days < 30) return t('sidebar.space.daysAgo', { count: days })
    return ''
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[320px] sm:w-[360px] flex flex-col gap-0 p-0 overflow-y-auto">
        {/* Header */}
        <SheetHeader className="px-4 py-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-sm font-semibold">
            <ProjectIcon iconType={space.iconType} icon={space.icon} iconColor={space.iconColor} size={16} />
            {space.name}
          </SheetTitle>
        </SheetHeader>

        {/* Metadata rows — same style as task sidebar panel */}
        <div className="flex flex-col px-4 py-2">
          {/* Created */}
          <div className="flex items-center gap-3 py-1.5">
            <span className="w-24 shrink-0 text-xs text-muted-foreground">{t('sidebar.space.metaCreated')}</span>
            <span className="text-sm text-foreground/80">
              {formatRelative(space.createdOn)}
              {' '}
              <span className="text-muted-foreground">{formatDate(space.createdOn)}</span>
            </span>
          </div>

          {/* Updated */}
          <div className="flex items-center gap-3 py-1.5">
            <span className="w-24 shrink-0 text-xs text-muted-foreground">{t('sidebar.space.metaLastUpdated')}</span>
            <span className="text-sm text-foreground/80">
              {formatRelative(space.updatedOn)}
              {' '}
              <span className="text-muted-foreground">{formatDate(space.updatedOn)}</span>
            </span>
          </div>
        </div>

        {/* Description section */}
        {space.description && (
          <div className="px-4 pt-2 pb-4">
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('sidebar.space.metaDescription')}
            </h3>
            <p className="text-sm text-foreground/80 whitespace-pre-wrap">{space.description}</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

// ─── Project details drawer ──────────────────────────────────────────────────

interface ProjectDetailsDrawerProps {
  project: ProjectDocument
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProjectDetailsDrawer({ project, open, onOpenChange }: ProjectDetailsDrawerProps) {
  const { t } = useTranslation('projects')
  const { date: fmtDate } = useDateTimeFormatters()
  const formatDate = (ts: number | undefined) => {
    if (!ts) return '—'
    return fmtDate(new Date(ts), { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatRelative = (ts: number | undefined) => {
    if (!ts) return ''
    const diff = Date.now() - ts
    const hours = Math.floor(diff / 3_600_000)
    const days = Math.floor(diff / 86_400_000)
    if (hours < 1) return t('sidebar.space.justNow')
    if (hours < 24) return t('sidebar.space.hoursAgo', { count: hours })
    if (days < 30) return t('sidebar.space.daysAgo', { count: days })
    return ''
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[320px] sm:w-[360px] flex flex-col gap-0 p-0 overflow-y-auto">
        <SheetHeader className="px-4 py-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2 text-sm font-semibold">
            <ProjectIcon iconType={project.iconType} icon={project.icon} iconColor={project.iconColor} size={16} />
            {project.name}
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col px-4 py-2">
          <div className="flex items-center gap-3 py-1.5">
            <span className="w-24 shrink-0 text-xs text-muted-foreground">{t('sidebar.space.metaCreated')}</span>
            <span className="text-sm text-foreground/80">
              {formatRelative(project.createdOn)}
              {' '}
              <span className="text-muted-foreground">{formatDate(project.createdOn)}</span>
            </span>
          </div>

          <div className="flex items-center gap-3 py-1.5">
            <span className="w-24 shrink-0 text-xs text-muted-foreground">{t('sidebar.space.metaLastUpdated')}</span>
            <span className="text-sm text-foreground/80">
              {formatRelative(project.updatedOn)}
              {' '}
              <span className="text-muted-foreground">{formatDate(project.updatedOn)}</span>
            </span>
          </div>
        </div>

        {project.description && (
          <div className="px-4 pt-2 pb-4">
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('sidebar.space.metaDescription')}
            </h3>
            <p className="text-sm text-foreground/80 whitespace-pre-wrap">{project.description}</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

// ─── Add project dialog ───────────────────────────────────────────────────────

interface AddProjectDialogProps {
  spaceId: string
  workspaceSlug: string
  spaceKey: string
  /** Sort order of the last existing sibling, used to append the new project at the end. */
  lastSiblingSortOrder: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddProjectDialog({
  spaceId,
  workspaceSlug,
  spaceKey,
  lastSiblingSortOrder,
  open,
  onOpenChange,
}: AddProjectDialogProps) {
  const { t } = useTranslation('projects')
  const { t: tCommon } = useTranslation('common')
  const navigate = useNavigate()
  const { createProject } = useProjectMutations()

  const projectFormSchema = useMemo(() => buildProjectFormSchema(t), [t])
  const form = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: { name: '', projectKey: '', icon: null, iconType: null, iconColor: null },
  })

  const handleSubmit = async (data: ProjectFormData) => {
    try {
      await createProject({
        id: crypto.randomUUID(),
        projectKey: data.projectKey,
        name: data.name,
        icon: data.icon,
        iconType: data.iconType,
        iconColor: data.iconColor,
        parentProjectId: spaceId,
        sortOrder: generateRankBetween(lastSiblingSortOrder, null),
      })
      form.reset()
      onOpenChange(false)
      toast.success(t('sidebar.toast.projectCreated'))
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
        params: { workspaceSlug, spaceKey, projectKey: data.projectKey.toLowerCase() },
      })
    } catch (error) {
      const fallback = t('sidebar.toast.projectCreateFailed')
      toast.error(error instanceof Error ? error.message || fallback : fallback)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) form.reset(); onOpenChange(o) }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sidebar.addProject.title')}</DialogTitle>
          <DialogDescription>{t('sidebar.addProject.description')}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid gap-2">
            <Label htmlFor="proj-name">{t('sidebar.addProject.nameLabel')}</Label>
            <div className="flex items-center gap-2">
              <UnifiedIconPicker
                iconType={form.watch('iconType')}
                icon={form.watch('icon')}
                iconColor={form.watch('iconColor')}
                onValueChange={(value) => {
                  form.setValue('icon', value?.icon ?? null)
                  form.setValue('iconType', value?.iconType ?? null)
                  form.setValue('iconColor', value?.iconColor ?? null)
                }}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label={t('sidebar.addProject.iconAria')}
                  disabled={form.formState.isSubmitting}
                >
                  <ProjectIcon iconType={form.watch('iconType')} icon={form.watch('icon')} iconColor={form.watch('iconColor')} size={18} />
                </Button>
              </UnifiedIconPicker>
              <div className="flex-1">
                <Input
                  id="proj-name"
                  {...form.register('name')}
                  placeholder={t('sidebar.addProject.namePlaceholder')}
                  autoFocus
                  disabled={form.formState.isSubmitting}
                />
                {form.formState.errors.name && (
                  <p className="text-xs text-destructive mt-1">{form.formState.errors.name.message}</p>
                )}
              </div>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="proj-key">{t('sidebar.addProject.keyLabel')}</Label>
            <Input
              id="proj-key"
              {...form.register('projectKey')}
              onChange={(e) => {
                form.setValue('projectKey', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))
              }}
              placeholder={t('sidebar.addProject.keyPlaceholder')}
              maxLength={10}
              disabled={form.formState.isSubmitting}
              className="w-1/2 font-mono"
            />
            {form.formState.errors.projectKey && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.projectKey.message}</p>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={form.formState.isSubmitting}>{tCommon('actions.cancel')}</Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? t('sidebar.addProject.submitting') : t('sidebar.addProject.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Star menu item (extracted to avoid hook-in-conditional) ──────────────────

export function SpaceStarMenuItem({ spaceId }: { spaceId: string }) {
  const { t } = useTranslation('projects')
  const { isStarred, starredItem } = useIsStarred('project', spaceId)
  const { toggleStar } = useStarredMutations()

  return (
    <DropdownMenuItem
      onSelect={() => { void toggleStar('project', spaceId, starredItem) }}
    >
      <Star className={cn('mr-2 size-4', isStarred && 'fill-yellow-400 text-yellow-400')} />
      {isStarred ? t('sidebar.removeFavorite') : t('sidebar.addFavorite')}
    </DropdownMenuItem>
  )
}
